import type { CutOffDate, ScheduleLegResponse, ScheduleResponse } from "@/types/api";
import {
  normalizeCutoffName,
  parseCutOffDates,
} from "@/lib/scheduleCutoffs";

export function scheduleCarrierCode(s: ScheduleResponse): string {
  return s.carrier_canonical_code || s.carrier_name;
}

export function scheduleSailingEtd(s: ScheduleResponse): string | null {
  const iso = s.origin_estimated_date ?? s.legs[0]?.departure_estimated_date;
  return iso ? iso.slice(0, 10) : null;
}

export function scheduleSailingEta(s: ScheduleResponse): string | null {
  const last = s.legs[s.legs.length - 1];
  const iso = s.dest_estimated_date ?? last?.arrival_estimated_date;
  return iso ? iso.slice(0, 10) : null;
}

export function scheduleSailingTransitDays(s: ScheduleResponse): number | null {
  if (s.transit_time != null && s.transit_time >= 0) {
    return s.transit_time;
  }
  const etd = scheduleSailingEtd(s);
  const eta = scheduleSailingEta(s);
  if (!etd || !eta) return null;
  const start = new Date(`${etd}T12:00:00`);
  const end = new Date(`${eta}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  return days > 0 ? days : null;
}

export function formatScheduleTransitDays(days: number): string {
  return `${days} ${days === 1 ? "day" : "days"}`;
}

export function formatScheduleLegPort(
  locode: string | null | undefined,
  name: string | null | undefined,
  showLocodes: boolean,
): string {
  if (name?.trim()) {
    const code = locode?.trim();
    if (showLocodes && code && name.trim() !== code) {
      return `${name.trim()} (${code})`;
    }
    return name.trim();
  }
  return locode?.trim() || "—";
}

export function collectScheduleCutoffs(sailing: ScheduleResponse): CutOffDate[] {
  const seen = new Set<string>();
  const out: CutOffDate[] = [];

  const add = (items: CutOffDate[]) => {
    for (const c of items) {
      if (!c.date_time) continue;
      const key = `${normalizeCutoffName(c.name)}|${c.date_time}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
  };

  add(parseCutOffDates(sailing.cut_off_dates));
  for (const leg of sailing.legs) {
    add(parseCutOffDates(leg.cut_off_dates));
  }

  return out.sort((a, b) => {
    const ta = new Date(a.date_time!).getTime();
    const tb = new Date(b.date_time!).getTime();
    if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
    return ta - tb;
  });
}

export function sortedScheduleLegs(
  sailing: ScheduleResponse,
): ScheduleLegResponse[] {
  return [...sailing.legs].sort((a, b) => a.leg_order - b.leg_order);
}

export function schedulePrimaryVessel(sailing: ScheduleResponse): string | undefined {
  return sortedScheduleLegs(sailing).find((l) => l.vessel_name?.trim())
    ?.vessel_name?.trim();
}
